import {FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import * as moment from 'moment';
import {AuthService} from './auth.service';
import {MatDialogRef} from '@angular/material/dialog';
import {InitializeSumsDialogComponent} from '../refrigerator-budget/initialize-sums-dialog/initialize-sums-dialog.component';
import * as FileSaver from 'file-saver';
import {CustomLoadingComponent} from '../common/custom-loading/custom-loading.component';
import {MatDialog} from '@angular/material';

@Injectable({
  providedIn: 'root'
})

export class CommonService {
  public tempDialogReference: any;
  public showCustomLoading: boolean;
  public loader: any;

  constructor(private httpClient: HttpClient, private authService: AuthService,
              public dialog: MatDialog) {
    this.showCustomLoading = false;
  }

  public toggleCustomLoader(show) {
    if (show && !this.loader) {
      this.loader = this.dialog.open(CustomLoadingComponent, {
        hasBackdrop: true,
        width: '20%',
        disableClose: true,
        position: {
          left: '40%'
        }
      });
    }

    if (!show) {
      this.loader.close();
      this.loader = null;
    }
  }

  private filterValues(filterValues: ReplaySubject<any[]>, formControl: FormControl, values: any[]): void {
    if (!values) {
      return;
    }

    let search = formControl.value;

    if (!search) {
      filterValues.next(values.slice());

      return;
    } else {
      search = search.toLowerCase();
    }

    filterValues.next(values.filter(value => value.name.toLowerCase().indexOf(search) > -1));
  }

  public findElement(element: any, elements: any[]): any {
    const elementIndex = elements.findIndex((data) => {
      return element === data.id;
    });

    return elements[elementIndex];
  }

  public formatDate(value: any): string {
    return value ? moment(value).format('DD/MM/YYYY') : '-';
  }

  public formatDateTime(value: any): string {
    return value ? moment(value).format('DD/MM/YYYY HH:mm:ss') : '-';
  }

  public getList(model: string) {
    return new Promise((resolve, reject) => {
      this.httpClient.get(`${model}?order=name asc`).subscribe((result) => {
        resolve(result);
      }, error => reject({message: 'No Data'}));
    });
  }

  public getSimpleList(model: string) {
    return new Promise((resolve, reject) => {
      this.httpClient.get(`${model}`).subscribe((result) => {
        resolve(result);
      }, error => reject({message: 'No Data'}));
    });
  }

  public getPDF(model: string, filename: string) {
    return new Promise((resolve, reject) => {
      this.httpClient.get(model, {
        responseType: 'blob' // This must be a Blob type
      }).subscribe(res => {
        // this.fileSaverService.save((res as any)._body, fileName);
        FileSaver.saveAs((res as Blob), filename, 'application/pdf');
      }, error => reject({message: 'No Data'}));
    });
  }

  public getListWithParameters(model: string, filter: string) {
    return new Promise((resolve, reject) => {
      this.httpClient.get(`${model}?filter=${filter}`).subscribe((result) => {
        resolve(result);
      }, error => {
        reject({message: error.message});
      });
    });
  }


  public deleteItem(model: string, parameter: any) {
    return new Promise((resolve, reject) => {
      this.httpClient.delete(`${model}?id=${parameter}`).subscribe((result) => {
        resolve(result);
      }, error => {
        reject({message: error.message});
      });
    });
  }

  public deleteItemWithParam(model: string) {
    return new Promise((resolve, reject) => {
      this.httpClient.delete(`${model}`).subscribe((result) => {
        resolve(result);
      }, error => {
        reject({message: error.message});
      });
    });
  }

  public deleteRecord(url: string) {
    return new Promise((resolve, reject) => {
      this.httpClient.delete(`${url}`).subscribe((result) => {
        resolve(result);
      }, error => {
        reject({message: error.message});
      });
    });
  }

  public initializeControls(filterValues: ReplaySubject<any[]>, formControl: FormControl, onDestroy: Subject<void>, values: any[]): void {
    formControl.valueChanges.pipe(takeUntil(onDestroy)).subscribe(() => {
      this.filterValues(filterValues, formControl, values);
    });
  }

  public insertObject(model: string, object: any): Promise<any> {
    return new Promise((resolve, reject) => {

      this.httpClient.post(`${model}`, object).subscribe((result) => {
        resolve(result);
      }, error => reject(error.message));
    });
  }

  public insertObjectForAutomations(model: string, object: any): Promise<any> {
    return new Promise((resolve, reject) => {

      this.httpClient.post(`${model}`, object).subscribe((result) => {
        resolve(result);
      }, error => reject(error));
    });
  }

  public insertItem(model: string, object: any): any {
    return new Promise((resolve, reject) => {
      this.httpClient.post(`${model}`, object).subscribe((result: any) => {
        resolve(result.insertId);
      }, error => reject(error.message));
    });
  }

  public isFormFieldValid(form: FormGroup, formField: string): boolean {
    return form.get(formField).touched && form.get(formField).invalid;
  }

  public setErrorMessage(form: FormGroup, formField: string, type?: string): string {
    switch (type) {
      case 'email':
        if (form.get(formField).errors.required) {
          return 'Υποχρεωτικό πεδίο.';
        }

        if (form.get(formField).errors.email) {
          return 'Λάθος τύπος email.';
        }

        break;
      case 'postalCode':
        if (form.get(formField).errors.required) {
          return 'Υποχρεωτικό πεδίο.';
        }

        if (form.get(formField).errors.minlength) {
          return 'Συμπληρώστε 5 ψηφία.';
        }

        break;
      case 'telephone':
        if (form.get(formField).errors.required) {
          return 'Υποχρεωτικό πεδίο.';
        }

        if (form.get(formField).errors.minlength) {
          return 'Συμπληρώστε 10 ψηφία.';
        }

        break;
      case 'temporaryPassword':
        if (form.get(formField).errors.required) {
          return 'Υποχρεωτικό πεδίο.';
        }

        if (form.get(formField).errors.minlength) {
          return 'Συμπληρώστε 6 ψηφία.';
        }

        break;
      case 'vatNumber':
        if (form.get(formField).errors.required) {
          return 'Υποχρεωτικό πεδίο.';
        }

        if (form.get(formField).errors.minlength) {
          return 'Συμπληρώστε 9 ψηφία.';
        }

        if (form.get(formField).errors.invalidVatNumber) {
          return 'Λάθος μορφή Α.Φ.Μ..';
        }

        if (form.get(formField).errors.uniqueVatNumber) {
          return 'Υπάρχει εταιρεία με το ίδιο Α.Φ.Μ..';
        }

        break;
      default:
        const field = form.get(formField);
        if (field.errors.required) {
          return 'Υποχρεωτικό πεδίο.';
        }

        if (field.errors.validField) {
          return field.errors.message;
        }

        break;
    }
  }

  public setPaginatorTexts(paginator: MatPaginator): MatPaginator {
    paginator._intl.itemsPerPageLabel = 'Εγγραφές Ανά Σελίδα';
    paginator._intl.firstPageLabel = 'Πρώτη Σελίδα';
    paginator._intl.lastPageLabel = 'Τελευταία Σελίδα';
    paginator._intl.nextPageLabel = 'Επόμενη Σελίδα';
    paginator._intl.previousPageLabel = 'Προηγούμενη Σελίδα';

    const greekRangeLabel = (page: number, pageSize: number, length: number) => {
      if (length === 0 || pageSize === 0) {
        return `0 από ${length}`;
      }

      length = Math.max(length, 0);

      const startIndex = page * pageSize;

      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;

      return `${startIndex + 1} - ${endIndex} από ${length}`;
    };

    paginator._intl.getRangeLabel = greekRangeLabel;

    return paginator;
  }

  public setValue(object: any): string {
    return object ? object : '-';
  }

  public showActions(user: any): boolean {
    return (this.authService.isUserBelongsTo('basicUser') ||
        this.authService.isUserBelongsTo('salesDirector') ||
        this.authService.isUserBelongsTo('callCenterManager') || this.authService.isUserBelongsTo('customerManager') ||
        this.authService.isUserBelongsTo('transportManager') || this.authService.isUserBelongsTo('technicalManager')
        || this.authService.isUserBelongsTo('hqManager')
        || this.authService.isUserBelongsTo('technicalManagerNew')) &&
      (user.role_alternate_name !== 'regionalDirector' && user.role_alternate_name !== 'salesInspector');
  }

  public canShowΗQ(user: any) {
    return this.authService.isUserBelongsTo('basicUser');
  }

  public canShowPlirotis() {
    return this.authService.isUserBelongsTo('basicUser') || this.authService.isUserBelongsTo('customerManager');
  }

  public canShowSettingsSyntelesteTab(user: any) {
    return (this.authService.isUserBelongsTo('basicUser') || this.authService.isUserBelongsTo('customerManager')
      || this.authService.isUserBelongsTo('salesDirector'));
  }

  public canShowSettingsAutomationsTab(user: any) {
    return (this.authService.isUserBelongsTo('basicUser') || this.authService.isUserBelongsTo('transportManager')
      || this.authService.isUserBelongsTo('technicalManager'));
  }

  public canShowSalesGroup(user: any) {
    return (this.authService.isUserBelongsTo('basicUser') || this.authService.isUserBelongsTo('salesDirector')
      || this.authService.isUserBelongsTo('customerManager'));
  }

  public canShowSettingsPromoTab(user: any) {
    return (this.authService.isUserBelongsTo('basicUser') || this.authService.isUserBelongsTo('technicalManager')
      || this.authService.isUserBelongsTo('salesDirector')
      || this.authService.isUserBelongsTo('customerManager') || this.authService.isUserBelongsTo('transportManager'));
  }

  public canShowSettingsPointsTab(user: any) {
    return (this.authService.isUserBelongsTo('basicUser') || this.authService.isUserBelongsTo('technicalManager')
      || this.authService.isUserBelongsTo('salesDirector') ||
      this.authService.isUserBelongsTo('transportManager'));
  }

  public canShowSettingsSparePartsTab(user: any) {
    return (this.authService.isUserBelongsTo('basicUser') || this.authService.isUserBelongsTo('technicalManager')
      || this.authService.isUserBelongsTo('salesDirector') ||
      this.authService.isUserBelongsTo('transportManager'));
  }

  public showTable(table: any[]): boolean {
    return table && table.length > 0;
  }

  public updateObject(model: string, object: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.httpClient.put(`${model}`, object).subscribe((result) => {
        resolve(result);
      }, error => reject(error));
    });
  }
}
